const PIN_SESSION_STORAGE_KEY = "kitchen-hub-pin-session";
const PIN_SESSION_ORG_CODE_KEY = "kitchen-hub-pin-session-org-code";
const PIN_SESSION_SITE_CODE_KEY = "kitchen-hub-pin-session-site-code";

type PinSessionStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

interface StoredPinSession {
  token: string;
  expires_at: string;
}

/**
 * Persists a chef's PIN-only sign-in token across reloads — this is the
 * credential for staff with no Supabase account at all, so it lives in
 * localStorage (not sessionStorage) to survive the phone's browser being
 * closed and reopened, matching "stays signed in until deactivated or
 * signed out" (confirmed with the project owner).
 */
export function readPinSession(storage: PinSessionStorage | null): string | null {
  try {
    const raw = storage?.getItem(PIN_SESSION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredPinSession;
    if (new Date(parsed.expires_at).getTime() <= Date.now()) {
      storage?.removeItem(PIN_SESSION_STORAGE_KEY);
      return null;
    }
    return parsed.token;
  } catch {
    return null;
  }
}

export function writePinSession(storage: PinSessionStorage | null, token: string | null, expiresAt?: string): void {
  try {
    if (token && expiresAt) storage?.setItem(PIN_SESSION_STORAGE_KEY, JSON.stringify({ token, expires_at: expiresAt }));
    else storage?.removeItem(PIN_SESSION_STORAGE_KEY);
  } catch {
    // The caller keeps its in-memory state; failed reads remain fail-closed.
  }
}

/** Remembers which org-code link this device was signed in from, so a
 * sign-out (or a revoked token) can bounce back to the same PIN pad
 * rather than a dead end. */
export function readPinSessionOrgCode(storage: PinSessionStorage | null): string | null {
  try {
    return storage?.getItem(PIN_SESSION_ORG_CODE_KEY) ?? null;
  } catch {
    return null;
  }
}

export function writePinSessionOrgCode(storage: PinSessionStorage | null, code: string | null): void {
  try {
    if (code) storage?.setItem(PIN_SESSION_ORG_CODE_KEY, code);
    else storage?.removeItem(PIN_SESSION_ORG_CODE_KEY);
  } catch {
    // Best-effort only.
  }
}

/** The site part of a per-site sign-in link, so a sign-out returns to that
 * site's shorter name list rather than everyone in the organisation. */
export function readPinSessionSiteCode(storage: PinSessionStorage | null): string | null {
  try {
    return storage?.getItem(PIN_SESSION_SITE_CODE_KEY) ?? null;
  } catch {
    return null;
  }
}

export function writePinSessionSiteCode(storage: PinSessionStorage | null, code: string | null): void {
  try {
    if (code) storage?.setItem(PIN_SESSION_SITE_CODE_KEY, code);
    else storage?.removeItem(PIN_SESSION_SITE_CODE_KEY);
  } catch {
    // Best-effort only.
  }
}
